import { Component, inject, OnDestroy, ChangeDetectionStrategy, DestroyRef } from '@angular/core'

import { DomController, Platform, ModalController } from '@ionic/angular'
import { ScreenOrientation } from '@awesome-cordova-plugins/screen-orientation/ngx'
import { filter, map, take, tap } from 'rxjs/operators'
import { NotificationService } from '@core/services/notifications/notification.service'

import { StatusBar, Style } from '@capacitor/status-bar'
import { Device } from '@capacitor/device'
import { SplashScreen } from '@capacitor/splash-screen'
import { AnalyticsHandlerService } from '@core/services/analytics/analytics-handler.service'
import { IosUpdaterService } from '@core/services/updater/services/ios-updater.service'
import { register } from 'swiper/element/bundle'
import { CoreAuthService } from '@core/auth/services/core-auth-service'
import { Store } from '@ngrx/store'
import { selectIsoLangWithAvailableLanguages, selectProfile } from '@core/store/selectors/user.selectors'
import { App } from '@capacitor/app'
import { disconnectSocket, reconnectSocket } from '@core/store/actions/socket.actions'
import { FirebaseCrashlytics } from '@capacitor-firebase/crashlytics'
import { SafeArea } from 'capacitor-plugin-safe-area'
import { environment } from '@env'
import { TranslateService } from '@ngx-translate/core'
import { IonApp, IonRouterOutlet } from '@ionic/angular/standalone'
import { OnboardingComponent } from '@features/onboarding/onboarding.component'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'

register()

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IonApp, IonRouterOutlet]
})
export class AppComponent implements OnDestroy {
  private _platform: Platform = inject(Platform)
  private readonly _translate: TranslateService = inject(TranslateService)
  private _screenOrientation: ScreenOrientation = inject(ScreenOrientation)
  private _notifications: NotificationService = inject(NotificationService)
  private _ionicAuth: CoreAuthService = inject(CoreAuthService)
  private readonly _analytics: AnalyticsHandlerService = inject(AnalyticsHandlerService)
  private readonly _iosUpdater: IosUpdaterService = inject(IosUpdaterService)
  private readonly _store: Store = inject(Store)
  private readonly _domCtrl: DomController = inject(DomController)
  private readonly _modalCtrl: ModalController = inject(ModalController)
  private readonly _destroyRef: DestroyRef = inject(DestroyRef)

  constructor() {
    this._initializeLanguage()

    try {
      void this._initializeApp()
    } catch (e) {
      console.error('ERROR', e)
      void FirebaseCrashlytics.recordException(e)
    }
  }

  private async _initializeApp() {
    await this._platform.ready()
    await SplashScreen.show()

    await this._setupAppPermissions()
    await this._notifications.initialize()

    await this._ionicAuth.init()

    await this._handleAppStateChange()
    this._setupAppWithProfile()
    this._handleLanguageChange()
    await this._setupDisplay()
    await SplashScreen.hide()
  }

  ngOnDestroy() {
    this._iosUpdater.destroy()
  }

  private _handleAppStateChange() {
    return App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) {
        this._store.dispatch(reconnectSocket())
      } else {
        this._store.dispatch(disconnectSocket())
      }
    })
  }

  private _setupAppWithProfile() {
    this._store
      .select(selectProfile)
      .pipe(
        filter((data) => !!data),
        take(1),
        tap(async (data) => {
          await this._notifications.setupLiveProNotifications(data.userId)
          await this._analytics.init(data)
          this._iosUpdater.init()

          if (data.onBoardingStep === 0) {
            const modal = await this._modalCtrl.create({
              component: OnboardingComponent,
              cssClass: 'fullscreen-modal',
              backdropDismiss: false
            })
            await modal.present()
          }
        })
      )
      .subscribe()
  }

  private _handleLanguageChange() {
    this._store
      .select(selectIsoLangWithAvailableLanguages)
      .pipe(
        filter(({ lang, languages }) => !!lang && !!languages?.length),
        map(({ lang, languages }) => {
          if (languages.some((l) => l.id === lang)) {
            return lang
          } else {
            return environment.i18nDefaultLang
          }
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe((lang) => {
        const shortLang = (lang || '').split('-')[0] || environment.i18nDefaultLang
        this._translate.use(shortLang)
      })
  }

  private async _setupAppPermissions() {
    await this._analytics.requestPermissions()
    await this._notifications.requestPermissions()
  }

  private async _setupDisplay() {
    if ((await Device.getInfo()).platform !== 'web') {
      StatusBar.setOverlaysWebView({ overlay: true }).catch((error) => console.error(error))
      StatusBar.setStyle({ style: Style.Dark }).catch((error) => console.error(error))
      SafeArea.getSafeAreaInsets()
        .then((data) => {
          this._domCtrl.write(() => {
            const { insets } = data
            for (const [key, value] of Object.entries(insets)) {
              document.documentElement.style.setProperty(`--ion-safe-area-${key}`, `${value}px`)
              document.documentElement.style.setProperty(`--mcm-safe-area-${key}`, `${value}px`)
            }
          })
        })
        .catch((error) => console.error(error))
    }

    this._screenOrientation.lock(this._screenOrientation.ORIENTATIONS.PORTRAIT).catch((error) => console.error(error))
  }

  private _initializeLanguage() {
    const defaultLang = environment.i18nDefaultLang.split('-')[0]
    this._translate.use(defaultLang)
  }
}
